<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <div class="card">
          <div
            class="card-header d-flex flex-wrap justify-content-between align-items-center"
          >
            <h4 class="card-title">
              {{ company.name_fr }}
              <BBadge
                v-if="company.status"
                :variant="statusVariant"
                class="ml-2 align-middle"
                :class="{ 'text-body': statusVariant !== 'secondary' }"
                >{{ company.status }}</BBadge
              >
            </h4>
            <!-- Same call and semantics as FeedCompanies.vue (ES-1733); body-colour text: white on the theme's success/danger paints < 4.5:1 -->
            <div class="btn-group">
              <BButton
                v-if="
                  company.status === 'pending' || company.status === 'rejected'
                "
                size="sm"
                variant="success"
                class="text-body"
                :disabled="statusUpdating !== null"
                @click.prevent="updateStatus('active')"
              >
                <BSpinner small v-if="statusUpdating === 'active'"></BSpinner>
                <i v-else class="fa fa-check" aria-hidden="true"></i>
                Approuver
              </BButton>
              <BButton
                v-if="
                  company.status === 'pending' || company.status === 'active'
                "
                size="sm"
                variant="danger"
                class="text-body"
                :disabled="statusUpdating !== null"
                @click.prevent="updateStatus('rejected')"
              >
                <BSpinner small v-if="statusUpdating === 'rejected'"></BSpinner>
                <i v-else class="fa fa-times" aria-hidden="true"></i>
                Rejeter
              </BButton>
            </div>
          </div>
          <div class="card-body">
            <ValidationAlert :errors="errors" />
            <BForm @submit.prevent="updateCompany">
              <BFormGroup label="LinkedIn Id" label-for="linkedin-id">
                <BFormInput
                  id="linkedin-id"
                  v-model="company.linkedin_id"
                ></BFormInput>
              </BFormGroup>
              <BFormGroup
                label="Nom de compagnie LinkedIn (flux LinkedIn)"
                label-for="linkedin-company-name"
                description="Remplace le nom de la compagnie dans le flux XML LinkedIn (balise company), pour ses offres seulement. Ex. regrouper les offres de Galvanisation Québec sous « Corbec ». Si vide, on utilise le nom de la compagnie."
              >
                <BFormInput
                  id="linkedin-company-name"
                  name="linkedin_company_name"
                  autocomplete="off"
                  v-model="company.linkedin_company_name"
                  maxlength="255"
                ></BFormInput>
              </BFormGroup>
              <BFormGroup label="Video (FR)" label-for="video-fr">
                <BFormInput
                  id="video-fr"
                  v-model="company.video_fr"
                ></BFormInput>
              </BFormGroup>
              <BFormGroup label="Video (EN)" label-for="video-en">
                <BFormInput
                  id="video-en"
                  v-model="company.video_en"
                ></BFormInput>
              </BFormGroup>
              <BFormGroup label="Email (compagnie)" label-for="company-email">
                <BFormInput
                  id="company-email"
                  type="email"
                  v-model="company.email"
                ></BFormInput>
              </BFormGroup>
              <BFormGroup
                label="Force External Url (Feed Out)"
                label-for="force_external_url_in_feed_out"
              >
                <BFormCheckbox
                  id="force_external_url_in_feed_out"
                  v-model="company.force_external_url_in_feed_out"
                ></BFormCheckbox>
              </BFormGroup>
              <BButton
                :disabled="isPosting"
                variant="secondary"
                @click="goBack"
              >
                Back
              </BButton>
              <BButton :disabled="isPosting" variant="success" type="submit">
                Save
              </BButton>
            </BForm>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header">
            <h4 class="card-title">Images</h4>
          </div>
          <div class="card-body">
            <LogoUpload
              :current-logo="company.logo"
              :upload-url="`/enroll_admin/companies/${this.companyId}/logo`"
            />
          </div>
        </div>
      </div>
    </div>
    <!-- Indeed Settings -->
    <div class="row">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header">
            <h4 class="card-title">Indeed</h4>
          </div>
          <div class="card-body">
            <BFormGroup
              label="Email du compte Indeed (recommandé — contact de la compagnie)"
              label-for="indeed-account-email"
              description="Utilisé comme contact des offres envoyées à Indeed. Si vide, on retombe sur l'email de la compagnie, puis sur l'email de contact par défaut."
            >
              <BFormInput
                id="indeed-account-email"
                type="email"
                v-model="company.indeed_account_email"
              ></BFormInput>
            </BFormGroup>
            <BFormGroup
              label="sourceName Indeed (chaîne exacte confirmée par Indeed)"
              label-for="indeed-source-name"
              description="À copier-coller tel que fourni par Indeed (ex. « Groupe Eclair »). Ce n'est PAS le slug : Indeed exige de réutiliser la source déjà enregistrée pour le client, sinon on crée un doublon. Si vide, on retombe sur le nom de la compagnie (name_fr). Ignoré tant qu'un sourceName global (bac à sable) est configuré."
            >
              <BFormInput
                id="indeed-source-name"
                v-model="company.indeed_source_name"
              ></BFormInput>
            </BFormGroup>
            <BFormGroup
              label="Feed XML Indeed activé (requis pour publier sur Indeed)"
              description="Doit être activé pour qu'une offre puisse être publiée sur Indeed. Active/désactive aussi la génération du feed XML de la compagnie."
            >
              <BFormCheckbox
                v-model="company.is_feed_enabled"
                switch
              ></BFormCheckbox>
            </BFormGroup>
            <BFormGroup label="Activer la synchro Indeed (push des offres)">
              <BFormCheckbox
                v-model="company.is_indeed_sync_enabled"
                switch
              ></BFormCheckbox>
            </BFormGroup>
            <BFormGroup label="Indeed Apply activé (opt-out client)">
              <BFormCheckbox
                v-model="company.is_indeed_apply_enabled"
                switch
              ></BFormCheckbox>
            </BFormGroup>
            <BButton
              :disabled="savingIndeed"
              variant="primary"
              class="mr-2"
              @click="saveIndeedSettings"
            >
              Enregistrer réglages Indeed
            </BButton>
            <BButton
              :disabled="pushingIndeed || !company.is_indeed_sync_enabled"
              variant="success"
              @click="pushCompanyToIndeed"
            >
              Pousser toutes les offres vers Indeed
            </BButton>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-md-12">
        <div class="card">
          <div class="card-body">
            <pre>{{ company }}</pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import {
  BForm,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BButton,
  BBadge,
  BSpinner
} from "bootstrap-vue";
import ValidationAlert from "@/components/Forms/ValidationAlert.vue";
import LogoUpload from "@/components/Companies/LogoUpload.vue";

export default {
  components: {
    LogoUpload,
    ValidationAlert,
    BForm,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BButton,
    BBadge,
    BSpinner
  },
  props: {
    companyId: {
      required: true,
      type: [String, Number]
    }
  },
  data() {
    return {
      company: {},
      isPosting: false,
      errors: [],
      savingIndeed: false,
      pushingIndeed: false,
      statusUpdating: null
    };
  },
  computed: {
    statusVariant() {
      return (
        { active: "success", pending: "warning", rejected: "danger" }[
          this.company.status
        ] || "secondary"
      );
    }
  },
  created() {
    this.$http
      .get(`/enroll_admin/companies/${this.companyId}`)
      .then(({ data }) => {
        // Le défaut garantit que la clé existe et reste réactive même si l'API
        // ne renvoie pas encore la colonne (migration non passée sur l'env).
        this.company = {
          indeed_source_name: null,
          linkedin_company_name: null,
          ...data
        };
      });
  },
  methods: {
    async updateStatus(status) {
      this.statusUpdating = status;
      try {
        await this.$http.put(
          `/enroll_admin/companies/${this.companyId}/status`,
          { status }
        );
      } catch (e) {
        this.$bvToast.toast("Échec du changement de statut", {
          title: "Compagnie",
          variant: "danger",
          solid: true
        });
        this.statusUpdating = null;
        return;
      }
      // The PUT persisted it: a failed re-read must not report a failure.
      this.company.status = status;
      try {
        // Re-read the stored status; only that key, so unsaved form edits survive.
        const { data } = await this.$http.get(
          `/enroll_admin/companies/${this.companyId}`
        );
        this.company.status = data.status;
      } catch (e) {
        // Keep the status the PUT accepted.
      }
      this.$bvToast.toast(`Statut : ${this.company.status}`, {
        title: "Compagnie",
        variant: "success",
        solid: true
      });
      this.statusUpdating = null;
    },
    updateCompany() {
      this.errors = [];
      this.isPosting = true;
      const payload = {
        linkedin_id: this.company.linkedin_id,
        linkedin_company_name: this.company.linkedin_company_name,
        video_fr: this.company.video_fr,
        video_en: this.company.video_en,
        email: this.company.email,
        force_external_url_in_feed_out: this.company
          .force_external_url_in_feed_out
      };
      this.$http
        .put(`/enroll_admin/companies/${this.companyId}`, payload)
        .then(() => {
          Vue.$toast.success("Company updated");
        })
        .catch(error => {
          if (error.response && error.response.status === 422) {
            this.errors = error.response.data;
          }
        })
        .finally(() => {
          this.isPosting = false;
        });
    },
    goBack() {
      this.$router.push({
        name: "companies",
        params: {
          pageHistory: this.$route.params.pageHistory
        }
      });
    },
    async saveIndeedSettings() {
      this.savingIndeed = true;
      try {
        const { data } = await this.$http.put(
          `/enroll_admin/companies/${this.companyId}/indeed-settings`,
          {
            is_feed_enabled: this.company.is_feed_enabled,
            is_indeed_sync_enabled: this.company.is_indeed_sync_enabled,
            is_indeed_apply_enabled: this.company.is_indeed_apply_enabled,
            indeed_account_email: this.company.indeed_account_email,
            indeed_source_name: this.company.indeed_source_name
          }
        );
        this.company.is_feed_enabled = data.is_feed_enabled;
        this.company.is_indeed_sync_enabled = data.is_indeed_sync_enabled;
        this.company.is_indeed_apply_enabled = data.is_indeed_apply_enabled;
        this.company.indeed_account_email = data.indeed_account_email;
        this.company.indeed_source_name = data.indeed_source_name;
        this.$bvToast.toast("Réglages Indeed enregistrés", {
          title: "Indeed",
          variant: "success",
          solid: true
        });
      } catch (e) {
        this.$bvToast.toast("Échec de l'enregistrement", {
          title: "Indeed",
          variant: "danger",
          solid: true
        });
      } finally {
        this.savingIndeed = false;
      }
    },
    async pushCompanyToIndeed() {
      this.pushingIndeed = true;
      try {
        const { data } = await this.$http.post(
          `/enroll_admin/companies/${this.companyId}/push-indeed`
        );
        this.$bvToast.toast(`Push lancé (${data.count} offres)`, {
          title: "Indeed",
          variant: "success",
          solid: true
        });
      } catch (e) {
        this.$bvToast.toast("Échec du push", {
          title: "Indeed",
          variant: "danger",
          solid: true
        });
      } finally {
        this.pushingIndeed = false;
      }
    }
  }
};
</script>
